import { useEffect, useContext, useState, createContext } from 'react'
import {
    SdkModuleContextType,
    ResourceType,
    BasicProviderProps,
    ResourceVersionType,
    FormSettingsType,
} from '../types';
import useDataClient from '../hooks/useDataClient';
import { AppContext } from './AppContext';
import { resourcesAdapter } from '../adapter';
import ErrorBoundary from '../components/error/ErrorBoundary';
import { DashboardContext } from './DashboardContext';

export const SdkModuleContext = createContext<SdkModuleContextType | null>(null);

const initialFormSettings: FormSettingsType = { show: false, icon: 'plus', title: '', submitLabel: '', type: null }

function SdkModuleProvider({ children }: BasicProviderProps) {
    const appContext = useContext(AppContext)
    const dashboardContext = useContext(DashboardContext)
    const deleteResourceClient = useDataClient<number>({ appContext })
    const deleteVersionClient = useDataClient<number>({ appContext })
    const newResourceClient = useDataClient<number>({ appContext })
    const editResourceClient = useDataClient<number>({ appContext })
    const updateVersionClient = useDataClient<number>({ appContext })
    const [formSettings, setFormSettings] = useState<FormSettingsType>(initialFormSettings)
    const [idRepo, setIdRepo] = useState<number>(0)
    const [sdkTarget, setSdkTarget] = useState<ResourceType | null>(null)
    const [sdkVersionTarget, setSdkVersionTarget] = useState<ResourceVersionType | null>(null)

    function reloadResources(message: string) {
        dashboardContext?.updateAppData('sdk')
        appContext?.setAppDialog({ message: message, type: 'success' })
        closeForm();
    }

    function addResource(newSdkRequest: ResourceType, files: File[]) {
        newResourceClient.executeRequest(resourcesAdapter.addResource(newSdkRequest, files))
    }

    function editResource(newSdkRequest: ResourceType) {
        editResourceClient.executeRequest(resourcesAdapter.editResource(newSdkRequest))
    }

    function confirmResourceDelete(item: ResourceType) {
        appContext?.setAppDialog({
            title: 'Aviso',
            message: `¿Desea suprimir el SDK ${item.label}?`,
            type: 'warning',
            onAccept: () => deleteResourceClient.executeRequest(resourcesAdapter.deleteResource(item))
        })
    }

    function addVersion(version: ResourceVersionType, files: File[]) {
        updateVersionClient.executeRequest(resourcesAdapter.updateVersion(version, files, sdkTarget?.id || 0))
    }

    function editVersion(version: ResourceVersionType, files: File[]) {
        updateVersionClient.executeRequest(resourcesAdapter.updateVersion(version, files))
    }

    function confirmVersionDelete(item: ResourceVersionType) {
        appContext?.setAppDialog({
            title: 'Aviso',
            message: `¿Desea suprimir la versión ${item.version}?`,
            type: 'warning',
            onAccept: () => deleteVersionClient.executeRequest(resourcesAdapter.deleteVersion(item))
        })
    }

    function addSdkForm() {
        setFormSettings({ show: true, title: 'Agregar SDK', icon: 'plus', submitLabel: 'Agregar', type: 'RA' })
    }

    function editSdkForm(target: ResourceType) {
        setSdkTarget(target)
        setFormSettings({ show: true, title: 'Editar SDK', icon: 'edit', submitLabel: 'Editar', type: 'RE' })
    }


    function addVersionForm(target: ResourceType) {
        setSdkTarget(target)
        setFormSettings({ show: true, title: 'Agregar Versión', icon: 'plus', submitLabel: 'Agregar', type: 'RVA' })
    }

    function editVersionForm(target: ResourceType, versionTarget: ResourceVersionType) {
        setSdkTarget(target)
        setSdkVersionTarget(versionTarget)
        setFormSettings({ show: true, title: 'Editar Versión', icon: 'edit', submitLabel: 'Editar', type: 'RVE' })
    }

    function closeForm() {
        setFormSettings(initialFormSettings)
        setSdkTarget(null)
        setSdkVersionTarget(null)
    }

    useEffect(() => {
        if (deleteResourceClient.status.data) {
            reloadResources('SDK eliminado')
        }
    }, [deleteResourceClient.status.data])

    useEffect(() => {
        if (deleteVersionClient.status.data) {
            reloadResources('Versión eliminada')
        }
    }, [deleteVersionClient.status.data])

    useEffect(() => {
        let requestAddedId = newResourceClient.status.data
        if (requestAddedId) {
            reloadResources(`Recurso agregado con id ${requestAddedId}`)
        }
    }, [newResourceClient.status.data])

    useEffect(() => {
        let requestAddedId = editResourceClient.status.data
        if (requestAddedId) {
            reloadResources(`Recurso editado`)
        }
    }, [editResourceClient.status.data])

    useEffect(() => {
        let requestAddedId = updateVersionClient.status.data
        if (requestAddedId) {
            reloadResources(`Versión actualizada`)
        }
    }, [updateVersionClient.status.data])

    return <ErrorBoundary>
        <SdkModuleContext.Provider value={{
            listData: dashboardContext?.appData['sdk'] || [],
            addResource,
            editResource,
            confirmResourceDelete,
            confirmVersionDelete,
            addVersion,
            editVersion,
            formSettings,
            sdkTarget,
            sdkVersionTarget,
            idRepo, setIdRepo,
            actions: { addSdkForm, editSdkForm, addVersionForm, editVersionForm },
            closeForm
        }}>
            {children}
        </SdkModuleContext.Provider>
    </ErrorBoundary>
};


export default SdkModuleProvider